import { useAuthentication } from '@ecosystem/newron-client-lib';
import { useMemo } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import { FilterPersistenceAsyncService } from './FilterPersistenceService';
import { UserFilter } from './Types';
import { usePageConfig } from '../../pageConfig/PagesConfigContext';
import { FilterKeys, Filters, PageType, SavedFilters } from '../../pageConfig/Types';
import { PreferenceAPIFilterPersistenceService } from '@utils/hook/saveFilter/PreferenceApiFilterPersistenceService';
import { cloneDeep, isEqual } from 'lodash';

export interface GetPersistedFilters {
  data: UserFilter[];
  refetch: (options?: {
    trowOnError?: boolean;
    cancelRefetch?: boolean;
  }) => Promise<UseQueryResult<UserFilter[], unknown>>;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

export function processCountryOptionsForFilter(filter: Filters, countryList: any[], savedFilterCountryList: string[]) {
  filter.options.forEach((option) => {
    let countryCode = countryList.find((f) => isEqual(f.name, option.label))?.id;
    if (countryCode) {
      option.value = countryCode;
      savedFilterCountryList.push(countryCode);
    }
  });
}

// Helper to process all country filters in persisted.values
export function processCountryFilters(persisted: UserFilter, countryList: any[], savedFilterCountryList: string[]) {
  persisted.values
    .filter((filter) => filter.filterKey === FilterKeys.COUNTRY)
    .forEach((e) => processCountryOptionsForFilter(e, countryList, savedFilterCountryList));
}

export const useGetPersistedFilters: (
  options?: UseQueryOptions<unknown, unknown, UserFilter[], unknown[]>,
  service?: FilterPersistenceAsyncService,
) => GetPersistedFilters = (
  options?: UseQueryOptions<unknown, unknown, UserFilter[], unknown[]>,
  service: FilterPersistenceAsyncService = PreferenceAPIFilterPersistenceService,
) => {
  const pagesConfig = usePageConfig();
  const { getAccessToken } = useAuthentication();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['Get filters'],
    queryFn: async () => {
      let userToken = await getAccessToken();
      if (!userToken) {
        throw new Error('Failed to get userToken');
      }

      let clientId = pagesConfig.pageType === PageType.RI_PRODUCT ? pagesConfig.hitlistsClientId : pagesConfig.clientId;
      const pop: UserFilter[] = await service.getFilters(userToken, clientId.toString());

      pop.forEach((userFilter) => {
        userFilter.values = Object.values(userFilter.values[0]) as Filters[];
      });

      let savedFilterCountryList: string[] = [];
      const persistedToSaveFilter: (persisted: UserFilter) => SavedFilters = (persisted) => {
        let countryList = cloneDeep(pagesConfig.clientCountries);
        processCountryFilters(persisted, countryList, savedFilterCountryList);
        return {
          id: persisted.id,
          name: persisted.name,
          createdAt: persisted.createdAt,
          filters: persisted.values,
          namespace: persisted.namespace,
          permissions: persisted.permissions,
          viewed: persisted.viewed,
        };
      };

      pagesConfig.savedFilters[PageType.RETAILER_OVERVIEW].savedFilters = pop.map(persistedToSaveFilter);
      pagesConfig.savedFilters[PageType.CATEGORY_LEADERBOARD].savedFilters = pop.map(persistedToSaveFilter);
      pagesConfig.savedFilters[PageType.OPPORTUNITY_REVIEW].savedFilters = pop.map(persistedToSaveFilter);
      pagesConfig.savedFilters[PageType.PERFORMANCE_ANALYSIS].savedFilters = pop.map(persistedToSaveFilter);
      pagesConfig.savedFilters[PageType.EXPLORE].savedFilters = pop.map(persistedToSaveFilter);
      pagesConfig.savedFilters[PageType.RI_PRODUCT].savedFilters = pop.map(persistedToSaveFilter);
      pagesConfig.savedFilters[PageType.TRADING_ASSISTANT].savedFilters = pop.map(persistedToSaveFilter);

      pagesConfig.setSavedFilterCountryList(Array.from(new Set(savedFilterCountryList)));

      return pop;
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  return useMemo(() => {
    return {
      data: data ?? [],
      isLoading,
      isError,
      error,
      refetch,
    };
  }, [data, isLoading, isError, error, refetch]);
};
